import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ROUTE_SCROLL_ROOT_SELECTOR = '[data-route-scroll-root]';

function resetRouteScroll() {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;

  document.querySelectorAll<HTMLElement>(ROUTE_SCROLL_ROOT_SELECTOR).forEach((root) => {
    root.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  });
}

function findHashTarget(hash: string) {
  if (!hash) return null;

  try {
    return document.getElementById(decodeURIComponent(hash.slice(1)));
  } catch {
    return document.getElementById(hash.slice(1));
  }
}

export function RouteScrollManager() {
  const location = useLocation();

  useLayoutEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    resetRouteScroll();

    let frameId: number | undefined;
    let observer: MutationObserver | undefined;
    let observerTimeout: number | undefined;

    const scrollToHashTarget = () => {
      const target = findHashTarget(location.hash);
      if (!target) return false;

      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
      return true;
    };

    if (location.hash) {
      frameId = window.requestAnimationFrame(() => {
        if (scrollToHashTarget()) return;

        observer = new MutationObserver(() => {
          if (scrollToHashTarget()) {
            observer?.disconnect();
            if (observerTimeout !== undefined) window.clearTimeout(observerTimeout);
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        observerTimeout = window.setTimeout(() => observer?.disconnect(), 1500);
      });
    }

    return () => {
      if (frameId !== undefined) window.cancelAnimationFrame(frameId);
      observer?.disconnect();
      if (observerTimeout !== undefined) window.clearTimeout(observerTimeout);
      window.history.scrollRestoration = previousRestoration;
    };
  }, [location.pathname, location.hash]);

  return null;
}
